import { NavLink } from 'react-router';
import {
  LayoutDashboard,
  GraduationCap,
  FileText,
  BookOpen,
  Award,
  Shield,
  Bell,
  BookPlus,
  X,
  ClipboardCheck,
  FileSearch,
  UserCheck,
  BarChart3,
  AlertTriangle,
  Users,
  UserPlus,
  GitPullRequest,
  ClipboardList,
  ShieldCheck,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useUnreadCount } from '@/api/notifications';
import type { UserRole } from '@/types';
import type { LucideIcon } from 'lucide-react';

type NavItem = {
  name: string;
  href: string;
  icon: LucideIcon;
  end?: boolean;
  external?: boolean;
};

type NavGroup = {
  label?: string;
  minRole?: UserRole;
  // Bug #2: gate by the instructor CAPABILITY (literal instructor OR admin+),
  // not the role ladder — a pure supervisor is not an instructor.
  requiresInstructor?: boolean;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { name: 'Inicio', href: '/', icon: LayoutDashboard, end: true },
      { name: 'Mi Capacitación', href: '/courses', icon: GraduationCap },
      { name: 'Documentos', href: '/documents', icon: FileText, end: true },
      { name: 'Lecturas', href: '/documents/readings', icon: BookOpen },
      { name: 'Certificados', href: '/certificates', icon: Award },
      { name: 'Cap. Externas', href: '/external-trainings', icon: BookPlus },
      { name: 'Competencia', href: '/competency', icon: Shield },
      { name: 'Notificaciones', href: '/notifications', icon: Bell },
    ],
  },
  {
    label: 'INSTRUCTOR',
    requiresInstructor: true,
    items: [
      { name: 'Crear Capacitación', href: '/instructor/courses', icon: GraduationCap },
      { name: 'Inscripciones', href: '/instructor/enrollments', icon: UserPlus },
      { name: 'Calificar Quizzes', href: '/instructor/quiz-grading', icon: ClipboardCheck },
      { name: 'Revisar Evidencias', href: '/instructor/evidence-review', icon: FileSearch },
      { name: 'Completar Practicas', href: '/instructor/complete-lessons', icon: UserCheck },
      { name: 'Observaciones', href: '/instructor/observations', icon: ClipboardCheck },
      { name: 'Reportes', href: '/instructor/reports', icon: BarChart3 },
      { name: 'Acc. Correctivas', href: '/instructor/corrective-actions', icon: AlertTriangle },
    ],
  },
  {
    label: 'SUPERVISOR',
    minRole: 'supervisor',
    items: [
      { name: 'Mi Equipo', href: '/supervisor/team', icon: Users },
      { name: 'Aprobar ICNs', href: '/supervisor/changes', icon: GitPullRequest },
      { name: 'Evaluaciones', href: '/supervisor/assessments', icon: ClipboardList },
      { name: 'Declaraciones', href: '/supervisor/declarations', icon: ShieldCheck },
    ],
  },
  {
    label: 'ADMINISTRADOR',
    minRole: 'admin',
    items: [
      {
        name: 'Panel de Admin',
        href: 'https://admin.deinsacr.net',
        icon: Settings,
        external: true,
      },
    ],
  },
];

export function Sidebar({ onClose }: { onClose?: () => void }) {
  const { hasMinRole, canInstruct } = usePermissions();
  const { data: unreadCount } = useUnreadCount();

  return (
    <aside className="flex h-full w-64 flex-col border-r border-sidebar-border bg-sidebar">
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        <img
          src="https://grupodim-lms.nyc3.cdn.digitaloceanspaces.com/logos/logo%20normal.png"
          alt="Grupo DIM"
          className="h-10 object-contain dark:hidden"
        />
        <img
          src="https://grupodim-lms.nyc3.cdn.digitaloceanspaces.com/logos/logo%20negativo.png"
          alt="Grupo DIM"
          className="hidden h-10 object-contain dark:block"
        />
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-sidebar-foreground hover:bg-accent lg:hidden"
            aria-label="Cerrar menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((group, groupIdx) => {
          if (group.minRole && !hasMinRole(group.minRole)) return null;
          if (group.requiresInstructor && !canInstruct) return null;

          return (
            <div key={groupIdx}>
              {group.label && (
                <div className="mb-2 mt-4 flex items-center gap-2 px-3">
                  <div className="h-px flex-1 bg-sidebar-border" />
                  <span className="text-[10px] font-semibold tracking-wider text-muted-foreground">
                    {group.label}
                  </span>
                  <div className="h-px flex-1 bg-sidebar-border" />
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) =>
                  item.external ? (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={onClose}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        'text-sidebar-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {item.name}
                    </a>
                  ) : (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      end={item.end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-sidebar-active text-sidebar-active-foreground'
                            : 'text-sidebar-foreground hover:bg-accent hover:text-accent-foreground',
                        )
                      }
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {item.name}
                      {item.href === '/notifications' && !!unreadCount && unreadCount > 0 && (
                        <span
                          aria-label={`${unreadCount} sin leer`}
                          className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold text-primary-foreground"
                        >
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </NavLink>
                  ),
                )}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-4">
        <p className="text-xs text-muted-foreground">Grupo DIM — Mi Capacitación v0.1.0</p>
      </div>
    </aside>
  );
}
